import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  Edit,
  ChevronLeft,
  ChevronRight,
  Copy,
  FileDown,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { ListLoader } from '@/components/shared/ListLoader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { paymentReturnService } from '@/services/paymentReturnService'
import { billsService } from '@/services/billsService'
import { refundsService } from '@/services/refundsService'
import { PaymentReturnListItem, PaymentReturnListResponse, BillVendor, RefundBill, BillListItem } from '@/api/types'
import { calendarToday, formatCalendarDate } from '@/lib/calendarDate'
import { SortOrderToggle } from '@/components/shared/SortOrderToggle'
import type { ListSortDir } from '@/lib/listSort'

export const PaymentReturnsList: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [paymentReturns, setPaymentReturns] = useState<PaymentReturnListItem[]>([])
  const [vendors, setVendors] = useState<BillVendor[]>([])
  const [bills, setBills] = useState<(RefundBill | BillListItem)[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [sortDir, setSortDir] = useState<ListSortDir>('desc')

  const branchId = user?.selectedBranch?.branchId

  // Load vendors and bills for mapping
  useEffect(() => {
    if (!branchId) return

    const loadLookups = async () => {
      try {
        // Load vendors
        const vendorsData = await billsService.getAllVendors(branchId)
        setVendors(vendorsData || [])

        // Load bills - try to get all bills with a large page size
        try {
          const billsResponse = await billsService.getBills(branchId, 1, 1000)
          const billsData = billsResponse.data || []
          console.log('🔵 [PaymentReturnsList] Loaded bills from billsService:', billsData.length)
          console.log('🔵 [PaymentReturnsList] Sample bill:', billsData[0])
          setBills(billsData)
        } catch (billsError) {
          // Fallback to refunds service if bills service fails
          console.warn('Failed to load bills from billsService, trying refundsService:', billsError)
          const refundsBillsData = await refundsService.getAllBills(branchId)
          console.log('🔵 [PaymentReturnsList] Loaded bills from refundsService:', refundsBillsData.length)
          setBills(refundsBillsData || [])
        }
      } catch (error: any) {
        console.error('Failed to load vendors/bills for mapping:', error)
        // Error handled silently - list will still work with IDs
      }
    }

    loadLookups()
  }, [branchId])

  // Helper functions to get names
  const getVendorName = (vendorId: number): string => {
    if (!vendorId) return 'N/A'
    const vendor = vendors.find(v => (v.vendorId || v.id) === vendorId)
    return vendor?.vendorName || vendor?.vendorNameEn || vendor?.vendorNameAr || `Vendor #${vendorId}`
  }

  const getBillNo = (billId: number): string => {
    if (!billId) return 'N/A'
    const bill = bills.find(b => {
      const bId = (b as any).billId || (b as any).id || (b as any).refBillId
      return bId === billId
    })
    if (bill) {
      const billNo = (bill as any).billNo || (bill as any).billNumber || (bill as any).billSerialNo
      if (billNo) {
        return billNo
      }
    }
    return `Bill #${billId}`
  }

  // Load payment returns
  useEffect(() => {
    if (!branchId) {
      return
    }

    const loadPaymentReturns = async () => {
      try {
        setLoading(true)
        const response: PaymentReturnListResponse = await paymentReturnService.getAllPaymentReturns(
          branchId!,
          pageNumber,
          pageSize,
          sortDir
        )
        setPaymentReturns(response.data || [])
        setTotalCount(response.totalRecords || 0)
        // Calculate total pages
        const calculatedTotalPages = response.totalRecords 
          ? Math.ceil(response.totalRecords / response.pageSize) 
          : 0
        setTotalPages(calculatedTotalPages)
      } catch (error: any) {
        const status = error.response?.status
        const msg = (error.response?.data?.message || '').toLowerCase()
        const isNoData = status === 404 || /not found|no data|no records/.test(msg)
        setPaymentReturns([])
        setTotalCount(0)
        setTotalPages(0)
        if (!isNoData) {
          await showError('Error', error.response?.data?.message || 'Failed to load payment returns', 5000)
        }
      } finally {
        setLoading(false)
      }
    }

    loadPaymentReturns()
  }, [branchId, pageNumber, pageSize, sortDir])

  const filteredPaymentReturns = useMemo(() => {
    if (!searchQuery.trim()) return paymentReturns
    const query = searchQuery.toLowerCase()
    return paymentReturns.filter((item) => {
      return (
        (item.returnNo || '').toLowerCase().includes(query) ||
        (item.billNo || '').toLowerCase().includes(query) ||
        (item.vendorName || '').toLowerCase().includes(query) ||
        String(item.id || '').includes(query)
      )
    })
  }, [paymentReturns, searchQuery])

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value))
    setPageNumber(1)
  }

  const handleEdit = (item: PaymentReturnListItem) => {
    const itemId = item.id
    if (!itemId) {
      toast.error('Invalid payment return ID')
      return
    }
    navigate(`/purchases/payment-returns/${itemId}/edit`)
  }

  const formatDate = (dateString?: string) => formatCalendarDate(dateString)

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return '0.00'
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  const csvEscape = (cell: string | number) => `"${String(cell).replace(/"/g, '""')}"`

  const handleCopy = async () => {
    if (filteredPaymentReturns.length === 0) {
      toast.error('Nothing to copy.')
      return
    }
    const header = ['Return No', 'Bill No', 'Vendor', 'Date', 'Amount'].join('\t')
    const lines = filteredPaymentReturns.map((r) =>
      [
        r.returnNo ?? '',
        r.billNo ?? getBillNo(Number((r as any).billId ?? 0)) ?? '',
        r.vendorName ?? getVendorName(Number((r as any).vendorId ?? 0)) ?? '',
        formatDate((r as any).returnDate ?? (r as any).date ?? ''),
        String((r as any).amount ?? ''),
      ].join('\t')
    )
    await navigator.clipboard.writeText([header, ...lines].join('\n'))
    toast.success('Copied to clipboard.')
  }

  const handleCsv = () => {
    if (filteredPaymentReturns.length === 0) {
      toast.error('Nothing to export.')
      return
    }
    const header = ['Return No', 'Bill No', 'Vendor', 'Date', 'Amount'].map(csvEscape).join(',')
    const body = filteredPaymentReturns
      .map((r) =>
        [
          r.returnNo ?? '',
          r.billNo ?? getBillNo(Number((r as any).billId ?? 0)) ?? '',
          r.vendorName ?? getVendorName(Number((r as any).vendorId ?? 0)) ?? '',
          formatDate((r as any).returnDate ?? (r as any).date ?? ''),
          String((r as any).amount ?? ''),
        ]
          .map(csvEscape)
          .join(',')
      )
      .join('\n')
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `payment_returns_${calendarToday()}.csv`
    link.click()
    window.URL.revokeObjectURL(url)
    toast.success('CSV downloaded.')
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title="Payment Returns"
        description="Payment Return Management"
      />

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search payment returns by return no, bill no, or vendor..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <SortOrderToggle
                value={sortDir}
                onChange={(next) => {
                  setSortDir(next)
                  setPageNumber(1)
                }}
              />
              <Button variant="outline" size="sm" onClick={() => void handleCopy()}>
                <Copy className="mr-2 h-4 w-4" />
                Copy
              </Button>
              <Button variant="outline" size="sm" onClick={handleCsv}>
                <FileDown className="mr-2 h-4 w-4" />
                CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <ListLoader message="Loading payment returns..." />
          ) : filteredPaymentReturns.length === 0 ? (
            <EmptyState
              title={searchQuery ? "No payment returns found" : "No payment returns found"}
              description={
                searchQuery
                  ? "Try adjusting your search query"
                  : "Get started by creating your first payment return."
              }
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        Return No
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        Bill No
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        Vendor
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        Date
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        Amount
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        Return Type
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center w-[120px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        Action
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPaymentReturns.map((item) => (
                      <TableRow
                        key={item.id}
                        className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
                      >
                        <TableCell className="px-4 py-2 text-sm text-center align-middle font-medium">
                          {item.returnNo || 'N/A'}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {item.billNo || getBillNo(item.billId)}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {item.vendorName || getVendorName(item.vendorId)}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {formatDate(item.date || undefined)}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {formatCurrency(item.amount)}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {item.returnType || 'N/A'}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-center align-middle">
                          <div className="flex justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(item)}
                              className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="border-t px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Showing {(pageNumber - 1) * pageSize + 1} to{' '}
                    {Math.min(pageNumber * pageSize, totalCount)} of {totalCount} entries
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">Page Size:</span>
                    <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber(prev => Math.max(1, prev - 1))}
                      disabled={pageNumber === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">
                      Page {pageNumber} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber(prev => Math.min(totalPages, prev + 1))}
                      disabled={pageNumber >= totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

